import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { FileText, Download, Trash2, Plus, Paperclip } from "lucide-react";

const input = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors [color-scheme:dark]";
const brl = (n) => Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

async function uploadFile(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

function ResidentSelect({ value, onChange }) {
  const { data } = useQuery({ queryKey: ["mgr-approved-sel"], queryFn: async () => (await api.get("/residents", { params: { status: "approved" } })).data });
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="pub-resident" className={input}>
      <option value="" className="bg-background">Selecione o morador</option>
      {data?.map((r) => <option key={r.id} value={r.id} className="bg-background">{r.name} — {r.tower}/{r.unit}</option>)}
    </select>
  );
}

export function PublicationsMgr({ type, label, allowIndividual = true }) {
  const qc = useQueryClient();
  const key = ["mgr-publications", type];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: async () => (await api.get("/publications", { params: { type } })).data });
  const [f, setF] = useState({ title: "", description: "", visibility: "general", resident_id: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!f.title.trim()) return toast.error("Informe o título.");
    if (f.visibility === "individual" && !f.resident_id) return toast.error("Selecione o morador destinatário.");
    setSaving(true);
    try {
      let file_url = null;
      if (file) file_url = await uploadFile(file);
      await api.post("/publications", { type, title: f.title, description: f.description, file_url, visibility: f.visibility, resident_id: f.visibility === "individual" ? f.resident_id : null });
      qc.invalidateQueries({ queryKey: key });
      setF({ title: "", description: "", visibility: "general", resident_id: "" }); setFile(null);
      toast.success(`${label} publicado!`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/publications/${id}`); qc.invalidateQueries({ queryKey: key }); toast.success("Removido."); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Publicar {label}</p>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pub-title" placeholder="Título" className={input} />
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pub-desc" rows={2} placeholder="Descrição / conteúdo" className={`${input} resize-none`} />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Paperclip className="w-4 h-4" /> {file ? file.name : "Anexar arquivo (opcional)"}
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="pub-file" />
        </label>
        {allowIndividual && (
          <div className="flex gap-2">
            <select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })} data-testid="pub-visibility" className={input}>
              <option value="general" className="bg-background">Geral (todos)</option>
              <option value="individual" className="bg-background">Individual</option>
            </select>
            {f.visibility === "individual" && <ResidentSelect value={f.resident_id} onChange={(v) => setF({ ...f, resident_id: v })} />}
          </div>
        )}
        <button onClick={create} disabled={saving} data-testid="pub-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60">{saving ? "Publicando..." : "Publicar"}</button>
      </div>
      {isLoading ? <Loading /> : !data?.length ? <EmptyState icon={FileText} title="Nada publicado ainda" /> : data.map((p) => (
        <div key={p.id} data-testid="mgr-publication" className="glass rounded-2xl p-4 flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{p.title}</p><p className="text-xs text-muted-foreground">{p.visibility === "individual" ? `Individual · ${p.resident_name || ""}` : "Geral"}</p></div>
          {p.file_url && <a href={fileUrl(p.file_url)} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 border border-white/10"><Download className="w-4 h-4" /></a>}
          <button onClick={() => del(p.id)} className="p-2 rounded-full bg-red-500/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

export function BoletosMgr() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-bills"], queryFn: async () => (await api.get("/bills")).data });
  const [f, setF] = useState({ resident_id: "", month: "", amount: "", due_date: "" });
  const create = async () => {
    if (!f.resident_id || !f.month || !f.amount || !f.due_date) return toast.error("Preencha todos os campos.");
    try { await api.post("/bills", { resident_id: f.resident_id, month: f.month, amount: Number(f.amount), due_date: f.due_date }); qc.invalidateQueries({ queryKey: ["mgr-bills"] }); setF({ resident_id: "", month: "", amount: "", due_date: "" }); toast.success("Boleto publicado para o morador!"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Publicar boleto</p>
        <ResidentSelect value={f.resident_id} onChange={(v) => setF({ ...f, resident_id: v })} />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} data-testid="bill-month" placeholder="Mês (ex: Junho/2026)" className={input} />
          <input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="bill-amount" placeholder="Valor" className={input} />
        </div>
        <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="bill-due" className={input} />
        <button onClick={create} data-testid="bill-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Publicar boleto</button>
      </div>
      {isLoading ? <Loading /> : data?.map((b) => (
        <div key={b.id} className="glass rounded-2xl p-4 flex items-center gap-3"><span className="text-xl">🧾</span><div className="flex-1"><p className="text-sm font-semibold">{b.month} — {brl(b.amount)}</p><p className="text-xs text-muted-foreground">{b.resident_name} · vence {new Date(b.due_date).toLocaleDateString("pt-BR")}</p></div><span className="text-xs capitalize text-muted-foreground">{b.status}</span></div>
      ))}
    </div>
  );
}

export function MultasMgr() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-fines"], queryFn: async () => (await api.get("/fines")).data });
  const [f, setF] = useState({ resident_id: "", type: "Advertência", reason: "" });
  const create = async () => {
    if (!f.resident_id || !f.reason.trim()) return toast.error("Selecione o morador e informe o motivo.");
    try { await api.post("/fines", { resident_id: f.resident_id, type: f.type, reason: f.reason }); qc.invalidateQueries({ queryKey: ["mgr-fines"] }); setF({ resident_id: "", type: "Advertência", reason: "" }); toast.success("Registro enviado ao morador (privado)."); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Registrar multa / advertência</p>
        <p className="text-xs text-muted-foreground">Visível apenas para o morador destinatário.</p>
        <ResidentSelect value={f.resident_id} onChange={(v) => setF({ ...f, resident_id: v })} />
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="fine-type" className={input}>
          <option className="bg-background">Advertência</option><option className="bg-background">Multa</option>
        </select>
        <textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="fine-reason" rows={2} placeholder="Motivo" className={`${input} resize-none`} />
        <button onClick={create} data-testid="fine-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Registrar</button>
      </div>
      {isLoading ? <Loading /> : data?.map((x) => (
        <div key={x.id} className="glass rounded-2xl p-4 flex items-center gap-3"><span className="text-xl">⚠️</span><div className="flex-1"><p className="text-sm font-semibold">{x.type} — {x.resident_name}</p><p className="text-xs text-muted-foreground">{x.reason}</p></div><span className="text-xs capitalize text-muted-foreground">{x.status}</span></div>
      ))}
    </div>
  );
}
